import React from "react";

/**
 * ChunkErrorBoundary
 * ─────────────────────────────────────────────────────────────
 * Wraps lazy-loaded routes so a chunk-load failure or runtime
 * error never leaves the user staring at an infinite Suspense
 * spinner. Recoverable by clicking "Reload page".
 *
 * Common triggers caught:
 *   • Network blip during dynamic import()
 *   • Bad import path / removed module
 *   • TypeError during component initial render
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Light logging — production should hook this into Sentry/PostHog.
    // eslint-disable-next-line no-console
    console.error("[ChunkErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    try {
      // Clear any service-worker / cache that might be holding a stale chunk.
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
    } catch {/* ignore */}
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 bg-[var(--bg-primary)] text-[var(--text-primary)]"
        data-testid="chunk-error-boundary"
      >
        <div className="max-w-md w-full text-center space-y-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)]">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-[var(--warning)]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold tracking-tight">
              Couldn't load this page
            </h2>
            <p className="text-sm text-[var(--text-secondary)]">
              A required code chunk failed to load. This can happen briefly
              after a new release. Reloading should fix it.
            </p>
          </div>

          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] transition-colors"
            data-testid="chunk-error-reload-btn"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
